import axios, { AxiosRequestConfig } from 'axios';
import type { Perimetre } from '../../types/api';
import { serverRefusalFrom } from '../../services/apiEnvelope';

/**
 * Un refus motivé du serveur (409 `{ code, message }`) devient une
 * `ServerRefusalError` dont le message s'affiche tel quel (fenêtre du dossier,
 * suppression) ; toute autre erreur est relancée telle quelle.
 */
const relancer = (error: unknown): never => {
  throw serverRefusalFrom(error) ?? error;
};

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Crée la configuration axios avec les headers d'authentification
 * IMPORTANT: Azure SWA peut modifier le header Authorization,
 * on envoie donc aussi 'accesstoken' qui n'est pas modifié
 */
const createAuthHeaders = (accessToken?: string): AxiosRequestConfig => ({
  headers: accessToken 
    ? { 
        Authorization: `Bearer ${accessToken}`,
        accesstoken: accessToken,  // Header non modifié par Azure SWA
        'Content-Type': 'application/json'
      } 
    : {}
});

export const perimetreService = {
  // Récupérer tous les périmètres
  async perimetre(accessToken?: string): Promise<Perimetre[]> {
    // Pas de limite pour récupérer tous les périmètres
    const response = await axios.get(`${API_BASE_URL}/perimetres?limit=0`, createAuthHeaders(accessToken));
    
    // L'API retourne { success: true, data: [...] }
    // On extrait le tableau de données
    return response.data?.data || [];
  },

  // Sauvegarder un périmètre (création ou mise à jour)
  async save(perimetre: Partial<Perimetre>, accessToken?: string): Promise<Perimetre> {
    const method = perimetre._id ? 'PUT' : 'POST';
    const url = perimetre._id 
      ? `${API_BASE_URL}/perimetres/${perimetre._id}` 
      : `${API_BASE_URL}/perimetres`;
    
    const response = await axios({
      method,
      url,
      data: perimetre,
      ...createAuthHeaders(accessToken)
    }).catch(relancer);
    
    // L'API retourne { success: true, data: {...} }
    // On extrait l'objet de données
    return response.data?.data;
  },

  /**
   * Description d'un dossier partagé avec moi (participant « modification ») :
   * la charge ne porte que `{ _id, description }` — le reste de la fiche
   * appartient au propriétaire (403 `PARTICIPANT_DESCRIPTION_SEULE` sinon).
   */
  async saveSharedDescription(perimetreId: string, description: string, accessToken?: string): Promise<Perimetre> {
    const response = await axios.put(
      `${API_BASE_URL}/perimetres/${perimetreId}`,
      { _id: perimetreId, description },
      createAuthHeaders(accessToken)
    ).catch(relancer);
    return response.data?.data;
  },

  // Supprimer un périmètre
  async delete(perimetreId: string, accessToken?: string): Promise<void> {
    await axios.delete(
      `${API_BASE_URL}/perimetres/${perimetreId}`, 
      createAuthHeaders(accessToken)
    ).catch(relancer);
  }
};
